import Vue from 'vue';
import TrackingService from '@animaapp/anima-tracking';
import store from '@/store';
import { addDebugEvent } from '@/services/bus';
import errorHandler from '@/services/errorHandler';
import { hasAnalyticsConsent } from '@/utils/consent';
import { initMixpanel, trackAnonymousMixpanelEvent } from '@/services/mixpanel';
import { guardDispatchers } from '@/services/trackingConsent';

export { initMixpanel };

const config = {
  web: true,
  clientId: 'com.animaapp.web',
  baseURL: process.env.TRACKING_BASE_URL
};

/*
 * The service instance is exposed as Vue.prototype.$tracking, and views call it
 * directly: Root.vue, Login.vue, LoginWithSSO.vue and Signup.vue all call
 * alias(), which POSTs the user id and the visitor id to
 * /analytics/register_alias. Guarding the two wrappers below therefore was not
 * enough - anything holding the raw service walked straight past them.
 *
 * The methods that dispatch are guarded on the instance itself, so every caller
 * is covered however it reached the service.
 *
 * The identity setters are deliberately left working. They only populate local
 * state, and keeping them means the service is ready to send correctly the
 * moment somebody accepts, rather than reporting an anonymous session.
 */
export const tracking = guardDispatchers(new TrackingService(config));
export const trackEvent = (eventName, params = {}) => {
  if (!eventName) {
    return;
  } else if (localStorage.getItem('animp')) {
    // if impersonating - don't track
    return;
  }

  if (!hasAnalyticsConsent()) {
    try {
      trackAnonymousMixpanelEvent(eventName);
    } catch (err) {
      errorHandler.captureException(err);
    }
    return;
  }

  const extraTrackingData = store.getters['tracking/extraData'];
  const isChromeExtension = store.state.omniview?.isChromeExtension;

  if (isChromeExtension) {
    let extensionName = store.state['omniview'].extensionName;

    // add this flag so we can detect if this event is coming from the chrome extension or not
    params.extensionName = extensionName;
    params.fromChromeExtension = true;
  }
  try {
    tracking.trackEvent(eventName, { ...extraTrackingData, ...params });
    Vue.prototype?.$hj && Vue.prototype?.$hj('event', eventName);
  } catch (err) {
    errorHandler.captureException(err);
  }

  addDebugEvent({ eventName, params: { ...extraTrackingData, ...params } });
};

export const trackUserProps = (props) => {
  // User properties are the most identifying thing we send, so they wait for
  // consent like everything else - and for the same impersonation check
  // trackEvent already makes, which this had been missing.
  if (!hasAnalyticsConsent() || localStorage.getItem('animp')) {
    return;
  }

  try {
    tracking.setUserProperties(props);
  } catch (err) {
    errorHandler.captureException(err);
  }
};

export const debugEvent = (eventName, params = {}) => {
  const extraTrackingData = store.getters['tracking/extraData'];
  addDebugEvent({ eventName, params: { ...extraTrackingData, ...params } });
};

// remove timeout until refactor for better measuring
export const measure = (label) => tracking.measureStart(`web-app.${label}`);
export const measureEnd = (label, props) => tracking.measureEnd(`web-app.${label}`, props);
